import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import {
  EFFECTIVE_ORG_ID_STORAGE_KEY,
  setEffectiveOrgId,
  setEffectiveOrgIdInMemory,
} from '@/services/api'
import { useOrganizations } from '@/hooks/useOrganizations'
import type { Organization } from '@/types'

interface OrgContextValue {
  /** The currently selected organization (for super_admins switching between orgs) */
  selectedOrg: Organization | null
  /** The effective org ID to use for API calls */
  effectiveOrgId: string
  /** Whether the user is a super_admin viewing another org */
  isViewingOtherOrg: boolean
  /** Select an organization (super_admin only) */
  selectOrg: (org: Organization | null) => void
  /** Clear the selected org and return to user's own org */
  clearSelectedOrg: () => void
}

const OrgContext = createContext<OrgContextValue | null>(null)

function readStoredSelectedOrgId(): string {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return ''
  }
  try {
    return window.localStorage.getItem(EFFECTIVE_ORG_ID_STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

interface OrgProviderProps {
  children: ReactNode
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'

  // Persisted ID is the source of truth across reloads. selectedOrg (the full
  // Organization object) is resolved from the organizations list once loaded.
  const [selectedOrgId, setSelectedOrgId] = useState<string>(() =>
    readStoredSelectedOrgId()
  )

  // Fetch the organizations list so we can resolve the persisted ID into a
  // full Organization object for the switcher UI. React Query dedupes the
  // query with any other call to useOrganizations(true).
  const { data: organizations } = useOrganizations(isSuperAdmin)

  // A persisted selection is only valid for super_admin users and only if the
  // org still exists on the backend. Derive validity rather than mutating state
  // in an effect so the computation stays reactive.
  const persistedIdIsValid = useMemo(() => {
    if (!isSuperAdmin || !selectedOrgId) return false
    if (!organizations) return true // assume valid until list loads
    return organizations.some((o) => o.id === selectedOrgId)
  }, [isSuperAdmin, selectedOrgId, organizations])

  const selectedOrg = useMemo<Organization | null>(() => {
    if (!persistedIdIsValid || !organizations) return null
    return organizations.find((o) => o.id === selectedOrgId) ?? null
  }, [persistedIdIsValid, selectedOrgId, organizations])

  // Effective org ID: persisted/selected org for super_admin, user's own org otherwise.
  const effectiveOrgId = useMemo(() => {
    if (isSuperAdmin) return persistedIdIsValid ? selectedOrgId : ''
    return user?.orgId ?? ''
  }, [isSuperAdmin, persistedIdIsValid, selectedOrgId, user?.orgId])

  // Keep the api-module's in-memory value aligned with the computed effective
  // org ID. We intentionally use the in-memory setter so a non-super-admin's
  // own orgId never leaks into the super-admin-selection localStorage key.
  // Explicit super_admin actions (selectOrg / clearSelectedOrg) still persist.
  useEffect(() => {
    if (!user) return
    setEffectiveOrgIdInMemory(effectiveOrgId)
  }, [user, effectiveOrgId])

  // If the persisted ID becomes stale (org deleted, or a non-super-admin user
  // signs in while a prior super-admin's key is still in storage), clear the
  // storage key. This is a side-effect-only cleanup — state stays derived.
  useEffect(() => {
    if (!user) return
    const stored =
      typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
        ? (() => {
            try {
              return window.localStorage.getItem(EFFECTIVE_ORG_ID_STORAGE_KEY)
            } catch {
              return null
            }
          })()
        : null
    if (!stored) return
    const shouldClear =
      !isSuperAdmin ||
      (organizations !== undefined && !organizations.some((o) => o.id === stored))
    if (shouldClear) {
      try {
        window.localStorage.removeItem(EFFECTIVE_ORG_ID_STORAGE_KEY)
      } catch {
        // storage unavailable
      }
    }
  }, [user, isSuperAdmin, organizations])

  const isViewingOtherOrg =
    isSuperAdmin && persistedIdIsValid && selectedOrgId !== user?.orgId

  const selectOrg = useCallback(
    (org: Organization | null) => {
      if (!isSuperAdmin) {
        console.warn('Only super_admin can switch organizations')
        return
      }
      const orgId = org?.id ?? ''
      setSelectedOrgId(orgId)
      setEffectiveOrgId(orgId)
    },
    [isSuperAdmin]
  )

  const clearSelectedOrg = useCallback(() => {
    setSelectedOrgId('')
    setEffectiveOrgId('')
  }, [])

  const value: OrgContextValue = {
    selectedOrg,
    effectiveOrgId,
    isViewingOtherOrg,
    selectOrg,
    clearSelectedOrg,
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

/**
 * Hook to access the organization context.
 * Must be used within an OrgProvider.
 */
export function useOrg(): OrgContextValue {
  const context = useContext(OrgContext)
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider')
  }
  return context
}
